import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useSite } from "@/context/SiteContext";
import { mediaUrl, track } from "@/lib/api";

function seoKey(pathname) {
  if (pathname === "/" || pathname === "") return "home";
  const parts = pathname.split("/").filter(Boolean);
  if (parts[0] === "services" && parts[1]) return `service-${parts[1]}`;
  return parts[0];
}

function setMeta(attr, key, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!href) return;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function injectScript(id, src, content) {
  if (document.getElementById(id)) return;
  const s = document.createElement("script");
  s.id = id;
  s.async = true;
  if (src) s.src = src;
  if (content) s.innerHTML = content;
  document.head.appendChild(s);
}

function injectAnalytics(a) {
  if (!a) return;
  if (a.ga4_enabled && a.ga4_id) {
    injectScript("svx-ga4", `https://www.googletagmanager.com/gtag/js?id=${a.ga4_id}`);
    injectScript(
      "svx-ga4-init",
      null,
      `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${a.ga4_id}');`
    );
  }
  if (a.gtm_enabled && a.gtm_id) {
    injectScript(
      "svx-gtm",
      null,
      `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${a.gtm_id}');`
    );
  }
  if (a.meta_enabled && a.meta_pixel_id) {
    injectScript(
      "svx-meta-pixel",
      null,
      `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${a.meta_pixel_id}');fbq('track','PageView');`
    );
  }
  if (a.gsc_enabled && a.gsc_verification) {
    setMeta("name", "google-site-verification", a.gsc_verification);
  }
  if (a.clarity_enabled && a.clarity_id) {
    injectScript(
      "svx-clarity",
      null,
      `(function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y)})(window,document,"clarity","script","${a.clarity_id}");`
    );
  }
  if (a.tiktok_enabled && a.tiktok_pixel_id) {
    injectScript(
      "svx-tiktok",
      null,
      `!function(w,d,t){w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"];ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.load=function(e,n){ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=e,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};var o=d.createElement("script");o.type="text/javascript",o.async=!0,o.src="https://analytics.tiktok.com/i18n/pixel/events.js";var a=d.getElementsByTagName("script")[0];a.parentNode.insertBefore(o,a)};ttq.load('${a.tiktok_pixel_id}');ttq.page();}(window,document,'ttq');`
    );
  }
  if (a.linkedin_enabled && a.linkedin_partner_id) {
    injectScript(
      "svx-linkedin",
      null,
      `_linkedin_partner_id="${a.linkedin_partner_id}";window._linkedin_data_partner_ids=window._linkedin_data_partner_ids||[];window._linkedin_data_partner_ids.push(_linkedin_partner_id);(function(){var s=document.getElementsByTagName("script")[0];var b=document.createElement("script");b.type="text/javascript";b.async=true;b.src="https://snap.licdn.com/li.lms-analytics/insight.min.js";s.parentNode.insertBefore(b,s)})();`
    );
  }
}

export default function SEO() {
  const { pathname } = useLocation();
  const { settings } = useSite();

  useEffect(() => {
    if (pathname.startsWith("/admin")) return;
    track("page_view", pathname);
  }, [pathname]);

  useEffect(() => {
    const brand = settings?.brand || {};
    const page = settings?.seo?.pages?.[seoKey(pathname)] || {};
    const name = brand.name || "Scalovix Media Agency";
    document.title = page.title || `${name} — ${brand.tagline || "Digital Agency"}`;
    setMeta("name", "description", page.description || "");
    setMeta("name", "keywords", page.keywords || "");
    setCanonical(page.canonical || "");
    const ogImage = mediaUrl(page.og_image || settings?.seo?.default_og || "");
    setMeta("property", "og:title", page.title || document.title);
    setMeta("property", "og:description", page.description || "");
    setMeta("property", "og:image", ogImage);
    setMeta("property", "og:url", page.canonical || window.location.href);
    setMeta("property", "og:type", "website");
    setMeta("property", "og:site_name", name);
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", page.title || document.title);
    setMeta("name", "twitter:description", page.description || "");
    setMeta("name", "twitter:image", ogImage);

    const favicon = brand.favicon_url ? mediaUrl(brand.favicon_url) : "/logo.jpg";
    let link = document.head.querySelector('link[rel="icon"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.href = favicon;
  }, [settings, pathname]);

  useEffect(() => {
    const apply = () => {
      let consent = "all";
      try {
        consent = localStorage.getItem("svx_consent") || "all";
      } catch (e) {}
      if (consent === "all") injectAnalytics(settings?.analytics);
    };
    apply();
    window.addEventListener("svx-consent-changed", apply);
    return () => window.removeEventListener("svx-consent-changed", apply);
  }, [settings?.analytics]);

  return null;
}
